/** @jsxImportSource @emotion/react */
import { SwapOutlined, TagOutlined } from '@ant-design/icons'
import React from 'react'
import Text from '../../../../components/ts-components/Text'
import { formatCurrency } from '@sportninja/common/utils/utils'
import {
  cardContainer,
  cardContent,
  infoRow,
  detailsColumn,
  costColumn,
  costDetails,
  divider,
  actionRow,
  changeDivisionButton,
  promoCodeButton,
} from './styles'

/**
 * Division information interface
 */
interface Division {
  name: string
  division_age?: { name: string }
  division_gender?: { name: string }
  team_cost?: number
  player_cost?: string
  deposit?: number
  player_deposit?: number | null
  division_age_v2?: string | null
}

/**
 * Coupon information interface
 */
interface CouponInfo {
  id: string
  code: string
  discount: number
  type: 'percentage' | 'amount'
  subtotal: number
  discount_amount?: number
}

/**
 * Props interface for the SelectedDivisionCard component
 */
interface SelectedDivisionCardProps {
  division: Division
  couponInfo?: CouponInfo | null
  isTeamRep: boolean
  colors: typeof import('@sportninja/common/constants/appColors').default
  currency?: { name: string }
  onChangeDivision?: () => void
  onTogglePromoCode?: () => void
  showPromoCode?: boolean
  isCanlan?: boolean
}

/**
 * SelectedDivisionCard displays the selected division information including
 * division details, costs, coupon information, and action buttons.
 *
 * This is a purely presentational component that receives all data as props
 * and delegates business logic to parent components.
 */
export const SelectedDivisionCard: React.FC<SelectedDivisionCardProps> = ({
  division,
  couponInfo,
  isTeamRep,
  colors,
  currency,
  onChangeDivision,
  onTogglePromoCode,
  showPromoCode = false,
  isCanlan = false,
}) => {
  // Calculate the cost based on registration type
  const baseCost = isTeamRep
    ? division.team_cost
    : typeof division.player_cost === 'string'
    ? parseFloat(division.player_cost) || 0
    : division.player_cost
  const deposit = isTeamRep ? division.deposit : division.player_deposit
  const formattedCost = formatCurrency(baseCost || 0, currency?.name)
  const formattedDeposit = deposit
    ? formatCurrency(deposit, currency?.name)
    : null
  const formattedSubtotal = couponInfo?.subtotal
    ? formatCurrency(couponInfo.subtotal, currency?.name)
    : formattedCost
  const formattedDiscountAmount = couponInfo?.discount_amount
    ? formatCurrency(couponInfo.discount_amount, currency?.name)
    : null

  return (
    <div css={cardContainer}>
      <Text
        variant='captionLarge'
        weight='semiBold'
        color={colors.NEUTRAL_0}
        style='align-self: flex-start;'
      >
        Selected Division
      </Text>

      <div css={cardContent}>
        {/* Main information row */}
        <div css={infoRow}>
          {/* Division details column */}
          <div css={detailsColumn}>
            <Text
              variant='heading5'
              color={colors.WHITE}
              weight='semiBold'
              style='align-self: stretch;'
            >
              {division.name}
            </Text>
            <Text
              variant='paragraphSmall'
              color={colors.MAGNESIUM}
              weight='regular'
            >
              Age: {division.division_age_v2 || 'N/A'}
            </Text>
            <Text
              variant='paragraphSmall'
              color={colors.MAGNESIUM}
              weight='regular'
            >
              Gender: {division.division_gender?.name || 'N/A'}
            </Text>
            {couponInfo && couponInfo.discount ? (
              <Text
                variant='paragraphMedium'
                color={colors.SUCCESS_100}
                weight='semiBold'
              >
                {couponInfo.type === 'percentage'
                  ? `Coupon Code: ${couponInfo.code} - ${couponInfo.discount}% off`
                  : `Coupon Code: ${couponInfo.code} - $${couponInfo.discount} off`}
              </Text>
            ) : null}
          </div>

          {/* Cost information column */}
          <div css={costColumn}>
            <div css={costDetails}>
              <Text
                variant='paragraphSmall'
                color={colors.MAGNESIUM}
                weight='regular'
              >
                Cost Per {isTeamRep ? 'Team' : 'Player'}
              </Text>
              <Text
                variant='heading6'
                color={colors.NEUTRAL_0}
                weight='semiBold'
              >
                {couponInfo?.id && couponInfo.id !== ''
                  ? formattedSubtotal
                  : formattedCost}
              </Text>
            </div>

            {formattedDeposit && (
              <Text
                variant='paragraphSmall'
                color={colors.MAGNESIUM}
                weight='regular'
              >
                Deposit: {formattedDeposit}
              </Text>
            )}

            {formattedDiscountAmount && (
              <Text
                variant='paragraphMedium'
                color={colors.SUCCESS_100}
                weight='semiBold'
                style='align-self: flex-end;'
              >
                -{formattedDiscountAmount}
              </Text>
            )}
          </div>
        </div>

        {/* Divider */}
        <div css={divider} />

        {/* Action buttons row */}
        <div css={actionRow}>
          <p onClick={onChangeDivision} css={changeDivisionButton()}>
            <SwapOutlined style={{ fontSize: 16 }} />
            Change Division
          </p>

          <p
            onClick={onTogglePromoCode}
            css={promoCodeButton(showPromoCode, isCanlan)}
          >
            <TagOutlined style={{ fontSize: 16 }} />
            Coupon Code
          </p>
        </div>
      </div>
    </div>
  )
}

export default SelectedDivisionCard
